import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/auth/LoginPage';
import DoctorSignUpPage from './pages/auth/DoctorSignUpPage';
import Dashboard from './pages/dashboard/Dashboard';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ProfilePage from './pages/profile/ProfilePage';
import EditAvailabilityPage from './pages/profile/EditAvailabilityPage';
import DoctorOnboardingPage from './pages/doctor-onboarding/DoctorOnboardingPage';
import DoctorReviewStatusPage from './pages/doctor-onboarding/DoctorReviewStatusPage';
import AIRequestsPage from './pages/ai-requests/AIRequestsPage';
import ConsultationDetailsPage from './pages/ai-requests/ConsultationDetailsPage';
import AppointmentsPage from './pages/appointments/AppointmentsPage';
import EditAppointmentPage from './pages/appointments/EditAppointmentPage';
import AppointmentDetailPage from './pages/appointments/AppointmentDetailPage';
import VideoConferencePage from './pages/video-conference/VideoConferencePage';
import PatientsPage from './pages/patients/PatientsPage';
import PatientDetailPage from './pages/patients/PatientDetailPage';
import PatientAppointmentsPage from './pages/patients/PatientAppointmentsPage';
import PatientDietPlansPage from './pages/patients/PatientDietPlansPage';
import PatientNutritionProfilePage from './pages/patients/PatientNutritionProfilePage';
import PatientInsightsPage from './pages/patients/PatientInsightsPage';
import PatientMessagesPage from './pages/patients/PatientMessagesPage';
import CreatePatientPage from './pages/patients/CreatePatientPage';
import DietPlanDetailPage from './pages/patients/DietPlanDetailPage';
import RequirementsConversationPage from './pages/patients/RequirementsConversationPage';
import MealPlanListPage from './pages/patients/MealPlanListPage';
import MealPlanCreatePage from './pages/patients/MealPlanCreatePage';
import MealPlanViewPage from './pages/patients/MealPlanViewPage';
import MealPlanEditPage from './pages/patients/MealPlanEditPage';
import MealPlanStandaloneViewPage from './pages/patients/MealPlanStandaloneViewPage';
import MealPlanVersionViewPage from './pages/patients/MealPlanVersionViewPage';
import BillingPage from './pages/billing/BillingPage';
import { authService } from './services/auth/authService';
import { getOnboardingStatus } from './services/doctor-onboarding/doctorOnboardingService';
import ServiceUnavailableModal from './components/common/ServiceUnavailableModal';
import { emitApiUiEvent, subscribeApiUiEvents } from './services/api/apiEvents';
import { useNavigate } from 'react-router-dom';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const ApprovedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [redirect, setRedirect] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const check = async () => {
      try {
        const status = await getOnboardingStatus();
        if (!isMounted) return;
        const statusVal = (status.onboarding_status || '').toLowerCase();
        if (statusVal === 'approved') {
          setRedirect(null);
        } else if (statusVal === 'submitted' || statusVal === 'rejected') {
          setRedirect('/doctor-review-status');
        } else {
          setRedirect('/doctor-onboarding');
        }
      } catch (err: any) {
        if (!isMounted) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Try refresh so we don't log doctor out on expired access token
          if (authService.getRefreshToken()) {
            try {
              await authService.refreshAccessToken();
              setRedirect(null);
              return;
            } catch (refreshErr: any) {
              const code = refreshErr?.response?.data?.detail?.error_code;
              if (code === 'refresh_token_expired' || code === 'refresh_token_revoked' || code === 'user_disabled' || code === 'token_invalid') {
                authService.clearTokens();
                setRedirect('/login');
                return;
              }
              // Refresh failed; fall through to redirect
            }
          }
          // Do not auto-logout; show popup and keep current route (session/refresh issue, not network).
          emitApiUiEvent({
            type: 'session_issue',
            message: 'Your session could not be verified. Please try again or log in again.',
          });
          setRedirect(null);
        } else {
          setRedirect(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (authService.isAuthenticated()) {
      check();
    } else {
      setLoading(false);
      setRedirect('/login');
    }
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
        Checking account status...
      </div>
    );
  }
  if (redirect) {
    return <Navigate to={redirect} replace />;
  }
  return <>{children}</>;
};

// Proactive refresh so doctor stays logged in at least 30 min (refresh every 15 min when tab is open)
const SESSION_KEEP_ALIVE_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

function App() {
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceModalTitle, setServiceModalTitle] = useState('Service unavailable');
  const [serviceModalMessage, setServiceModalMessage] = useState('Network is down or service is not reachable. Please try again.');
  const [serviceModalRequiresRelogin, setServiceModalRequiresRelogin] = useState(false);
  const [serviceModalActionLabel, setServiceModalActionLabel] = useState<string | undefined>();
  const [serviceModalOnAction, setServiceModalOnAction] = useState<(() => void) | undefined>();

  // Auto-fetch CSRF token on app load if user is authenticated
  useEffect(() => {
    const initializeAuth = async () => {
      if (authService.isAuthenticated()) {
        // Ensure CSRF token is available
        await authService.ensureCsrfToken();
      }
    };

    initializeAuth();
  }, []);

  // Track last modal type so we can redirect to login when user dismisses session issue
  const [lastModalEventType, setLastModalEventType] = useState<'service_unavailable' | 'session_issue' | 'insufficient_wallet_balance' | null>(null);
  const refreshFailureStreakRef = useRef(0);

  // Global popup for network/service/session issues (do not force logout)
  useEffect(() => {
    const unsubscribe = subscribeApiUiEvents((evt) => {
      setServiceModalTitle(
        evt.type === 'service_unavailable' ? 'Service unavailable' : 
        evt.type === 'insufficient_wallet_balance' ? 'Session issue' : 'Session issue' // Keeps title exactly like the screenshot
      );
      setServiceModalMessage(typeof evt.message === 'string' ? evt.message : JSON.stringify(evt.message));
      setServiceModalRequiresRelogin(evt.type === 'session_issue');
      setLastModalEventType(evt.type as any);
      if (evt.type === 'insufficient_wallet_balance') {
        setServiceModalActionLabel('Go to Wallet');
        setServiceModalOnAction(() => () => {
          setServiceModalOpen(false);
          window.location.href = '/billing';
        });
      } else {
        setServiceModalActionLabel(undefined);
        setServiceModalOnAction(undefined);
      }
      setServiceModalOpen(true);
    });
    return () => unsubscribe();
  }, []);

  // Proactively refresh using refresh_token so doctor stays logged in 30+ min; refresh based on keys, don't ask to login
  useEffect(() => {
    const id = setInterval(async () => {
      if (!authService.isAuthenticated() || !authService.getRefreshToken()) return;
      if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
      try {
        await authService.refreshAccessToken();
        refreshFailureStreakRef.current = 0;
      } catch {
        // Notify only after consecutive failures to avoid transient-noise.
        refreshFailureStreakRef.current += 1;
        if (refreshFailureStreakRef.current >= 2) {
          emitApiUiEvent({
            type: 'session_issue',
            message: 'Session refresh is temporarily unavailable. Please continue working and retry in a moment.',
          });
          refreshFailureStreakRef.current = 0;
        }
      }
    }, SESSION_KEEP_ALIVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <Router>
      <ServiceUnavailableModal
        open={serviceModalOpen}
        title={serviceModalTitle}
        message={serviceModalMessage}
        requiresRelogin={serviceModalRequiresRelogin}
        actionLabel={serviceModalActionLabel}
        onAction={serviceModalOnAction}
        onRetry={() => {
          setServiceModalOpen(false);
          setLastModalEventType(null);
        }}
        onRelogin={() => {
          setServiceModalOpen(false);
          setLastModalEventType(null);
          authService.clearTokens();
          window.location.replace('/login?error=session_expired');
        }}
        onClose={() => {
          setServiceModalOpen(false);
          setLastModalEventType(null);
        }}
      />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/doctor-signup" element={<DoctorSignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/dashboard"
          element={
            <ApprovedRoute>
              <Dashboard />
            </ApprovedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ApprovedRoute>
              <ProfilePage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ApprovedRoute>
              <BillingPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/doctor-onboarding"
          element={
            <ProtectedRoute>
              <DoctorOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/doctor-review-status"
          element={
            <ProtectedRoute>
              <DoctorReviewStatusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/availability"
          element={
            <ApprovedRoute>
              <EditAvailabilityPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/ai-requests"
          element={
            <ApprovedRoute>
              <AIRequestsPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/ai-requests/:id"
          element={
            <ApprovedRoute>
              <ConsultationDetailsPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/appointments"
          element={
            <ApprovedRoute>
              <AppointmentsPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/appointments/:id"
          element={
            <ApprovedRoute>
              <AppointmentDetailPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/appointments/:id/edit"
          element={
            <ApprovedRoute>
              <EditAppointmentPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/video-conference/:appointmentId"
          element={
            <ApprovedRoute>
              <VideoConferencePage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients"
          element={
            <ApprovedRoute>
              <PatientsPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/create"
          element={
            <ApprovedRoute>
              <CreatePatientPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/appointments"
          element={
            <ApprovedRoute>
              <PatientAppointmentsPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/diet-plans"
          element={
            <ApprovedRoute>
              <PatientDietPlansPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/nutrition-profile"
          element={
            <ApprovedRoute>
              <PatientNutritionProfilePage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/insights"
          element={
            <ApprovedRoute>
              <PatientInsightsPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/messages"
          element={
            <ApprovedRoute>
              <PatientMessagesPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId"
          element={
            <ApprovedRoute>
              <PatientDetailPage />
            </ApprovedRoute>
          }
        />
        {/* Meal plan pages under diet plan: list, create, view, edit (more specific first) */}
        <Route
          path="/patients/:patientId/diet-plan/:goalId/daily-meals/new"
          element={
            <ApprovedRoute>
              <MealPlanCreatePage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/diet-plan/:goalId/daily-meals/:planId/edit"
          element={
            <ApprovedRoute>
              <MealPlanEditPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/diet-plan/:goalId/daily-meals/:planId"
          element={
            <ApprovedRoute>
              <MealPlanViewPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/diet-plan/:goalId/daily-meals"
          element={
            <ApprovedRoute>
              <MealPlanListPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/diet-plan/:goalId/conversation"
          element={
            <ApprovedRoute>
              <RequirementsConversationPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/diet-plan/:goalId/:section"
          element={
            <ApprovedRoute>
              <DietPlanDetailPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/diet-plan/:goalId"
          element={
            <ApprovedRoute>
              <DietPlanDetailPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/daily-meals-plan/:planId/version"
          element={
            <ApprovedRoute>
              <MealPlanVersionViewPage />
            </ApprovedRoute>
          }
        />
        <Route
          path="/patients/:patientId/daily-meals-plan/:planId"
          element={
            <ApprovedRoute>
              <MealPlanStandaloneViewPage />
            </ApprovedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
}

export default App;

